const { cartesianTransform } = require('espros-image-parser');
const { makeSurface } = require('./surface');
const { makePointCloud } = require('./point-cloud');
const { depthArrays, smoothDepths } = require('./depths');
const { feedMaskPixelTypes } = require('./feed-masks');

const _isValidPixel = ({ x, y, image }) => x >= 0 && x < image.width && y >= 0 && y < image.height;

const _coordinatesToPixelNumber = ({ x, y, image }) => y * image.width + x;


/**
 * Creates a surface (i.e. a set of triangles) from a SensorImage.
 * Each vertex is recorded in the x, y, and z arrays.
 * Each face's vertex indices are stored in the i, j and k arrays.
 *
 * While SensorImage's use mm for distances, x y and z values in
 * the surface are converted to meters
 *
 * @param {SensorImage} sensorImage  a SensorImage object
 * @param feedMask  a feed mask array of arrays from the INO algorithm
 * @param distanceScale scaling factor to use for distances from camera (defaults to 1)
 * @param pixelTypes  feed mask values to use (i.e. feed vs. bin wall, etc.)
 * @returns Surface
 */
exports.makeSurfaceFromReading = function (sensorImage, feedMask, {
  distanceScale = 1,
  pixelTypes = [feedMaskPixelTypes.FEED],
} = {}) {
  if (!Number.isFinite(distanceScale)) {
    throw new Error('invalid distance scale');
  }
  if (!Array.isArray(feedMask)) {
    throw new Error('feed mask is required');
  }

  const feedMaskIncludes = (x, y) => pixelTypes.includes(feedMask[y][x]);

  const data = {
    x: [],
    y: [],
    z: [],
    i: [],
    j: [],
    k: [],
  };

  const depths = memoizedSmoothDepths(sensorImage);

  for (let y = 0; y < sensorImage.height; y++) {
    for (let x = 0; x < sensorImage.width; x++) {
      // Add pixel data to x, y, and z arrays:
      if (Number.isFinite(depths[y][x]) && feedMaskIncludes(x, y)) {
        const cartesianPixel = cartesianTransform(sensorImage, x, y, {
          depthOverride: depths[y][x] * distanceScale,
        });
        data.x.push(cartesianPixel.x / 1000);
        data.y.push(cartesianPixel.y / 1000);
        data.z.push(-cartesianPixel.z / 1000);
      } else {
        data.x.push(null);
        data.y.push(null);
        data.z.push(null);
      }
    }
  }

  for (let y = 0; y < sensorImage.height; y++) {
    for (let x = 0; x < sensorImage.width; x++) {
      // Next need to add data about which vertices are connected as surfaces.
      // Each surface is represented as a triangle using the index of pixels in the x, y, z arrays
      // ------------------------------------
      // |           | (+0,-1)  |            |
      // |           |    a     |            |
      // ------------------------------------
      // | (-1,+0)   | (+0,+0)  |  (+1,+0)   |
      // |    d      |    m     |     b      |
      // ------------------------------------
      // |           | (+0,+1)  |            |
      // |           |    c     |            |
      // ------------------------------------

      const pixelOffsets = {
        a: {
          xDiff: 0,
          yDiff: -1,
        },
        b: {
          xDiff: 1,
          yDiff: 0,
        },
        c: {
          xDiff: 0,
          yDiff: 1,
        },
        d: {
          xDiff: -1,
          yDiff: 0,
        },
        m: {
          xDiff: 0,
          yDiff: 0,
        },
      };
      const triangles = [['a', 'm', 'b'], ['b', 'm', 'c'], ['c', 'm', 'd'], ['d', 'm', 'a']];

      triangles.forEach(triangle => {
        const trianglePixels = triangle.map(letter => {
          const {
            xDiff,
            yDiff,
          } = pixelOffsets[letter];
          return {
            x: x + xDiff,
            y: y + yDiff,
            image: sensorImage,
          };
        });

        if (trianglePixels.every(_isValidPixel) && trianglePixels.every(({
          x,
          y,
        }) => feedMaskIncludes(x, y))) {
          const i = _coordinatesToPixelNumber(trianglePixels[0]);
          const j = _coordinatesToPixelNumber(trianglePixels[1]);
          const k = _coordinatesToPixelNumber(trianglePixels[2]);

          if (data.x[i] && data.x[j] && data.x[k]) {
            data.i.push(i);
            data.j.push(j);
            data.k.push(k);
          }
        }
      });
    }
  }

  return makeSurface(makePointCloud(data.x, data.y, data.z), data.i, data.j, data.k);
};

/**
 * Helper function to map a SensorImage's (x,y) coordinate to the x, y, and z arrays of a surface
 */
exports.sensorPixelCoordinatesToSurfaceIndex = function (x, y, width) {
  return width * y + x;
};

let _lastSensorImage = null;
let _lastSmoothedDepths = null;
// Memoize just the last response from this method
const memoizedSmoothDepths = (sensorImage) => {
  if (sensorImage === _lastSensorImage) {
    return _lastSmoothedDepths;
  }
  _lastSmoothedDepths = smoothDepths(depthArrays(sensorImage));
  _lastSensorImage = sensorImage;
  return _lastSmoothedDepths;
};
